import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SecurityService } from 'src/app/services/security/security.service';

@Component({
  selector: 'app-setup-2fa-modal',
  templateUrl: './setup-2fa-modal.component.html',
  styleUrls: ['./setup-2fa-modal.component.css']
})
export class Setup2faModalComponent implements OnInit {

  @Input() authToken: string = ''; // Token para hacer llamadas sin guardarlo en localStorage
  @Input() preselectedMethod: 'authenticator' | 'sms' | null = null; // Método preseleccionado desde login
  @Output() onComplete = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();

  step: 'method' | 'loading' | 'phone-input' | 'scan' | 'verify' | 'backup' | 'complete' = 'method';
  selectedMethod: 'authenticator' | 'sms' | null = null;
  form: FormGroup;
  qrCode: string = '';
  secret: string = '';
  backupCodes: string[] = [];
  errors: any[] = [];
  submit_disabled = false;
  phoneNumber: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private securityService: SecurityService
  ) {
    this.form = this.formBuilder.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  async ngOnInit(): Promise<void> {
    // Si hay método preseleccionado, iniciar automáticamente
    if (this.preselectedMethod) {
      console.log('🔐 Método preseleccionado:', this.preselectedMethod);
      this.selectMethod(this.preselectedMethod);
    }
    // Si no, mostrar pantalla de selección de método
  }

  selectMethod(method: 'authenticator' | 'sms'): void {
    this.selectedMethod = method;
    this.step = 'loading';

    if (method === 'authenticator') {
      this.initSetupAuthenticator();
    } else {
      this.initSetupSMS();
    }
  }

  async initSetupAuthenticator(): Promise<void> {
    try {
      console.log('🔑 Iniciando setup 2FA con token:', this.authToken ? 'Token disponible' : 'Sin token');

      if (!this.authToken) {
        this.errors = [{ msg: 'Error: No hay token de autenticación disponible' }];
        this.step = 'method';
        return;
      }

      const resp = await this.securityService.setup2FAWithToken(this.authToken);
      console.log('📊 Respuesta setup 2FA:', resp);

      if (resp.ok) {
        this.qrCode = resp.data.qrCode;
        this.secret = resp.data.secret;
        console.log('✅ QR Code y secret recibidos');
        this.step = 'scan';
      }
    } catch (error: any) {
      console.error('❌ Error en setup 2FA:', error);
      this.errors = [{ msg: error?.error?.msg || 'Error al iniciar configuración de 2FA' }];
      this.step = 'scan';
    }
  }

  async initSetupSMS(): Promise<void> {
    // Mostrar paso de ingreso de teléfono
    this.step = 'phone-input';
  }

  async sendSMSCode(): Promise<void> {
    if (!this.phoneNumber || this.phoneNumber.length < 10) {
      this.errors = [{ msg: 'Por favor ingresa un número de teléfono válido (10 dígitos)' }];
      return;
    }

    this.submit_disabled = true;
    this.errors = [];

    try {
      // Agregar +58 automáticamente si el usuario no lo incluyó
      let fullPhoneNumber = this.phoneNumber.trim();

      // Remover el 0 inicial si existe (ej: 04141234567 -> 4141234567)
      if (fullPhoneNumber.startsWith('0')) {
        fullPhoneNumber = fullPhoneNumber.substring(1);
      }

      // Agregar +58 al inicio
      fullPhoneNumber = '+58' + fullPhoneNumber;

      console.log('📱 Número formateado:', fullPhoneNumber);

      // Llamar al backend para enviar código SMS
      const resp = await this.securityService.setupSMS2FAWithToken(this.authToken, fullPhoneNumber);

      if (resp.ok) {
        console.log('✅ Código SMS enviado');
        this.step = 'verify';
      }
    } catch (error: any) {
      this.errors = [{ msg: error?.error?.msg || 'Error al enviar código SMS' }];
    } finally {
      this.submit_disabled = false;
    }
  }

  goToVerify(): void {
    this.step = 'verify';
    this.errors = [];
  }

  async verifyCode(): Promise<void> {
    this.errors = [];

    if (this.form.valid && !this.submit_disabled) {
      this.submit_disabled = true;
      const code = this.form.get('code')?.value;

      try {
        if (this.selectedMethod === 'authenticator') {
          const resp = await this.securityService.verifySetup2FAWithToken(code, this.authToken);

          if (resp.ok) {
            this.backupCodes = resp.data.backupCodes;
            this.step = 'backup';
          }
        } else if (this.selectedMethod === 'sms') {
          // Verificar código SMS y activar 2FA
          const resp = await this.securityService.verifySetupSMS2FAWithToken(this.authToken, code);

          if (resp.ok) {
            console.log('✅ SMS 2FA activado exitosamente');
            console.log('🔑 Token recibido:', resp.token);

            // SMS 2FA no tiene códigos de respaldo, completar directamente
            this.step = 'complete';

            // Emitir evento de completado con el token para que el login component lo maneje
            this.onComplete.emit({ token: resp.token, user: resp.user });
          }
        }
        this.submit_disabled = false;
      } catch (error: any) {
        if (error?.error?.msg) {
          this.errors = [{ msg: error.error.msg }];
        } else {
          this.errors = [{ msg: 'Código incorrecto. Intenta nuevamente.' }];
        }
        this.submit_disabled = false;
      }
    }
  }

  downloadBackupCodes(): void {
    const text = this.backupCodes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'creditotal-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  copyBackupCodes(): void {
    const text = this.backupCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      alert('Códigos copiados al portapapeles');
    });
  }

  complete(): void {
    this.step = 'complete';
    setTimeout(() => {
      this.onComplete.emit({ success: true });
    }, 2000);
  }

  cancel(): void {
    this.onCancel.emit();
  }

  backToMethodSelection(): void {
    // Si hay método preseleccionado (viene desde login), cancelar en lugar de volver al selector interno
    if (this.preselectedMethod) {
      this.cancel();
      return;
    }

    // Si no hay método preseleccionado, volver al selector interno
    this.step = 'method';
    this.selectedMethod = null;
    this.errors = [];
    this.form.reset();
  }
}
